// eslint-disable-next-line require-jsdoc
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// IDS Components
import Dropdown, { MenuItem } from '@ids-ts/dropdown';
import Button from '@ids/button';
import { useDispatch } from 'react-redux';
import { ReduxEvent } from '../../../../assets/pagedata/constant';
import { addToCart } from '../../../services/productDataService';
import Loader from '../../Common/Loader';
import { ColorPicker } from '../../Common/ColorPicker';
import {
  isProductOutOfStock,
  sortColorOnStock
} from '../../Util/CommonUtils';

const accentProp = 'accent';
const priceProp = 'price';
const productsWithAccents = ['FOLDER-01', 'FOLDER-02'];

// eslint-disable-next-line require-jsdoc
export const AddToCartColorPickerPricingSection = (props) => {
  const { pageData, loading, error, pricing, outOfStockItems = [] } = props;
  const [colorPicked, setColorPicked] = useState(
    pageData.Department.PricingInformation.DefaultColor,
  );
  const [outOfStockSingleSku, setOutOfStockSingleSku] = useState(null);
  let isOutOfStock;

  const getAccents = () => {
    return pricing.length > 0
      ? [...new Set(pricing.filter((x) => x.Color === colorPicked).map((c) => c.Accent))]
          .sort()
          .reverse()
      : [];
  };

  const [selectedAccent, setSelectedAccent] = useState(
    getAccents().length > 0 ? getAccents()[0] : null,
  );


  const getPrices = () => {
    switch (pageData.Department.PFID) {
      case 'FOLDER-03':
      case 'FOLDER-04':
        return pricing.length > 0 ? pricing.filter((x) => x.Color === colorPicked) : [];

      default:
        return pricing.length > 0
          ? pricing.filter((x) => x.Accent === selectedAccent && x.Color === colorPicked)
          : [];
    }
  };
  const [selectedPrice, setSelectedPrice] = useState(null);

  const [addToCartClicked, setAddToCartClicked] = useState(false);

  const [showLoader, setShowLoader] = useState(false);

  const dispatch = useDispatch();

  const addToCartSuccess = (pageInfo) => {
    props.logToIMWebLogger(
      'info',
      `ProductPage -> AddToCartPricingSection: onAddToCartClicked success`,
    );
    dispatch({
      type: ReduxEvent.PAGE_DATA,
      data: {
        pageData: {
          cartCount: pageInfo.CartCount,
          login: pageInfo.login,
          promotionMessage: pageInfo.PromotionMessage,
        },
      },
    });
    setShowLoader(false);
    props.history.push('/cart');
  };

  const addToCartFailed = (err) => {
    props.logToIMWebLogger(
      'error',
      `ProductPage -> AddToCartPricingSection: onAddToCartClicked failed. Reason: ${err}`,
    );

    props.displayToast('Some error occured. Please try again.');
    setShowLoader(false);
  };

  const onAddToCartClicked = () => {
    setAddToCartClicked(true);

    if (!selectedPrice) {
      return;
    }

    setShowLoader(true);

    props.logToIMWebLogger(
      'info',
      `ProductPage -> AddToCartPricingSection: onAddToCartClicked called`,
    );

    const params = {
      IsCustomizable: '0',
      PfId: pageData.Department.PFID,
      SKU: selectedPrice,
    };

    addToCart(params, addToCartSuccess, addToCartFailed);
  };

  const getPlaceHolder = (val) => {
    if (loading) {
      return 'Loading....';
    }

    if (error) {
      return 'Error';
    }

    switch (val) {
      case accentProp:
        if (!colorPicked) {
          return 'Select a folder color';
        }
        return 'Select accent';

      case priceProp:
      default:
        if (!selectedAccent) {
          return 'Select a accent to continue';
        }
        return 'Select value';
    }
  };

  const getDropDownLabel = (val) => {
    switch (val) {
      case accentProp:
        return `Accent`;

      default:
        return `Quantity and Price`;
    }
  };

  const showAccent = () => {
    return productsWithAccents.includes(pageData.Department.PFID);
  };

  const getWarningText = () => {
    if (showAccent()) {
      return addToCartClicked && (!selectedPrice || !selectedAccent)
        ? 'Please select a value.'
        : null;
    }
    return addToCartClicked && !selectedPrice ? 'Please select a value.' : null;
  };

  const handleSingleSkuOutOfStock = (sku) => {
    if ( sku && sku.length > 0) {
      const outOfStockStatusSku = isProductOutOfStock(
        {
          pfid: null,
          sku,
        },
        outOfStockItems,
      );
      setOutOfStockSingleSku(outOfStockStatusSku);
    }
  };

  useEffect(() => {
    const skuDefault = getPrices().length > 0 ? getPrices()[0].Sku : null;
    setSelectedPrice(skuDefault);
    handleSingleSkuOutOfStock(skuDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outOfStockItems]);

  const setDefaultPrice = (color, accent) => {
    let prices = [];
    let defaultSku;
    if (showAccent()) {
      prices =
        accent && pricing.length > 0
          ? pricing.filter((x) => x.Accent === accent && x.Color === color)
          : [];
      defaultSku = prices.length > 0 ? prices[0].Sku : null;
      setSelectedPrice(defaultSku);
      handleSingleSkuOutOfStock(defaultSku);
    } else {
      prices = pricing.length > 0 ? pricing.filter((x) => x.Color === color) : [];
      defaultSku = prices.length > 0 ? prices[0].Sku : null;
      setSelectedPrice(defaultSku);
      handleSingleSkuOutOfStock(defaultSku);
    }
  };

  const onAccentChanged = (val) => {
    setOutOfStockSingleSku(null);
    setSelectedAccent(val);
    setDefaultPrice(colorPicked, val);
  };

  const onColorPickedCallback = (color) => {
    setOutOfStockSingleSku(null);
    setColorPicked(color);

    let accents = [];
    if (showAccent()) {
      accents = [...new Set(pricing.filter((x) => x.Color === color).map((c) => c.Accent))]
        .sort()
        .reverse();
      try {
        accents = sortColorOnStock(pricing, accents, color);
      } catch (err) {
        props.logToIMWebLogger(
          'Error',
          `${err} - Out of stock at AddToColorPickerPricingSection.jsx when the accent is not loading`,
        );
      }
      setSelectedAccent(accents.length > 0 ? accents[0] : null);
    }
    setDefaultPrice(color, accents.length > 0 ? accents[0] : null);
    props.callback(color);
  };

  const handleProductOutOfStock = () => {
    const priceArr = getPrices();
    isOutOfStock = {
      displayOutOfStock: false,
    };
    if (
      outOfStockItems &&
      outOfStockItems.length >= 1 &&
      priceArr &&
      priceArr.length > 0
    ) {
      const outOfStockList = priceArr.filter((eachsku) => eachsku.OutOfStock);
      if (priceArr.length === outOfStockList.length) {
        const outOfStockStatus = isProductOutOfStock(
          {
            pfid: null,
            sku: `${priceArr[0].Sku}`,
          },
          outOfStockItems,
        );
        isOutOfStock = outOfStockStatus;
      } else {
        isOutOfStock = {
          displayOutOfStock: false,
        };
      }
      return isOutOfStock;
    }
    return isOutOfStock;
  };

  const handleQuantityChange = (e) => {
    setSelectedPrice(e);
    handleSingleSkuOutOfStock(e);
  };

  return (
    <div>
      <ColorPicker
        allItem={pricing}
        supportedColors={pageData.Department.PricingInformation.Colors}
        defaultColor={pageData.Department.PricingInformation.DefaultColor}
        onColorPicked={onColorPickedCallback}
        pfid={pageData.Department.PFID}
      />

      {showAccent() && (
        <Dropdown
          label={getDropDownLabel(accentProp)}
          theme="quickbooks"
          buttonType="secondaryGhost"
          onChange={(e) => onAccentChanged(e.target.value)}
          value={selectedAccent}
          className="margin-bottom-20 dropdown-background-color-white-label"
          placeholder={getPlaceHolder(accentProp)}
          errorText={error ? 'Unable to fetch the value.' : null}
          warningText={getWarningText()}
        >
          {getAccents().map((p, key) => {
            return (
              <MenuItem key={`${key}`} value={`${p}`}>
                {`${p}`}
              </MenuItem>
            );
          })}
        </Dropdown>
      )}

      {handleProductOutOfStock() && isOutOfStock && isOutOfStock.displayOutOfStock && (
        <div className="product-unavailable-msg margin-bottom-20">
          {isOutOfStock.outOfStockMessage}
        </div>
      )}
      {handleProductOutOfStock() && isOutOfStock && !isOutOfStock.displayOutOfStock && (
        <>
          <Dropdown
            label={getDropDownLabel(priceProp)}
            theme="quickbooks"
            buttonType="secondaryGhost"
            onChange={(e) => handleQuantityChange(e.target.value)}
            value={selectedPrice}
            className="margin-bottom-20 dropdown-background-color-white-label"
            placeholder={getPlaceHolder(priceProp)}
            errorText={error ? 'Unable to fetch the value.' : null}
            warningText={getWarningText('price')}
          >
            {getPrices().map((q, key) => {
              return (
                <MenuItem key={`${key}`} value={`${q.Sku}`}>
                  {`${q.Quantity} for ${q.DiscPrice ? q.DiscPrice : q.ListPrice}`}
                </MenuItem>
              );
            })}
          </Dropdown>
          {outOfStockSingleSku?.displayOutOfStock ? (
            <div className="product-unavailable-msg margin-bottom-20">
              {outOfStockSingleSku.outOfStockMessage}
            </div>
          ) : (
            <Button
              theme="intuit"
              className="margin-bottom-20 pricing-btn"
              onClick={onAddToCartClicked}
              data-wa-link="Add to cart"
              data-ui-object="button"
              data-refer-ui-access-point={`add_to_cart| ${props.contentVariable.toLowerCase()}`}
            >
              Add to cart
            </Button>
          )}
        </>
      )}

      <p className="p2-regular bottom-statement">
        Orders typically ship within 48 hours. Custom orders may require up to six days. Rush orders
        available. Please call <span className="font-wgt-500">(866) 570-3842.</span>
      </p>

      <Loader show={showLoader} size={80} loadingText="Please wait.." />
    </div>
  );
};

AddToCartColorPickerPricingSection.propTypes = {
  pageData: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  pricing: PropTypes.array.isRequired,
  logToIMWebLogger: PropTypes.func.isRequired,
};
